import { useCallback } from 'react';

import { useDisclosure } from '@frontend/legacy-components';

import type { DxfViewerSession } from 'modules/common/viewer/dxfViewer/session/types';
import { useReadableStore } from 'modules/common/viewer/dxfViewer/session/useSessionState';

export type DxfMappingCancel = {
  /** Leaves mapping mode, asking first when there are edits to discard. */
  onCancel: () => void;
  discardDialog: {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
  };
};

/**
 * Cancel for the mapping toolbar. Leaving mapping mode is screen state, not an
 * entity action: Save is `dxfMapping.save` (see `DxfMappingActionsRoot`).
 */
export const useDxfMappingCancel = (
  session: DxfViewerSession,
): DxfMappingCancel => {
  const isDirty = useReadableStore(session.hasUnsavedMapping);
  const {
    isOpen: isDiscardDialogOpen,
    onOpen: onOpenDiscardDialog,
    onClose: onCloseDiscardDialog,
  } = useDisclosure();

  const leaveMapping = useCallback(() => {
    session.mapping.revert();
    session.mode.set('view');
  }, [session]);

  const onCancel = useCallback(() => {
    // Leaving with nothing to lose is immediate — the spec is explicit that only a
    // cancel with edits is worth interrupting someone for.
    if (isDirty) {
      onOpenDiscardDialog();
      return;
    }
    leaveMapping();
  }, [isDirty, onOpenDiscardDialog, leaveMapping]);

  const onConfirmDiscard = useCallback(() => {
    onCloseDiscardDialog();
    leaveMapping();
  }, [onCloseDiscardDialog, leaveMapping]);

  return {
    onCancel,
    discardDialog: {
      isOpen: isDiscardDialogOpen,
      onClose: onCloseDiscardDialog,
      onConfirm: onConfirmDiscard,
    },
  };
};
